import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logo from "@/assets/logo.png";

const AcceptInvitation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState<"loading" | "success" | "error" | "needsAuth">("loading");
  const [error, setError] = useState<string>("");
  const [companyId, setCompanyId] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      checkAndAccept();
    } else {
      setStatus("error");
      setError("Invalid invitation link");
    }
  }, [token]);

  const checkAndAccept = async () => {
    try {
      // Check if user is logged in
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setStatus("needsAuth");
        return;
      }

      // Accept the invitation
      const { data, error } = await supabase.rpc("accept_invitation", {
        _token: token!,
        _user_id: user.id,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; company_id?: string };
      
      if (!result.success) {
        throw new Error(result.error || "Failed to accept invitation");
      }

      setCompanyId(result.company_id || null);
      setStatus("success");
      toast.success("Invitation accepted successfully!");
    } catch (err: any) {
      setStatus("error");
      setError(err.message || "Failed to accept invitation");
    }
  };

  const handleLogin = () => {
    // Store the invitation URL to redirect back after login
    sessionStorage.setItem("invitationRedirect", window.location.href);
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="flex items-center justify-center gap-3">
          <img src={logo} alt="Memo" className="h-10 w-10 rounded-lg" />
          <span className="text-2xl font-bold">Memo</span>
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle>
              {status === "loading" && "Processing invitation..."}
              {status === "success" && "Welcome to the team!"}
              {status === "error" && "Invitation error"}
              {status === "needsAuth" && "Sign in required"}
            </CardTitle>
            <CardDescription>
              {status === "loading" && "Please wait while we process your invitation"}
              {status === "success" && "You have been added to the company"}
              {status === "error" && error}
              {status === "needsAuth" && "Please sign in or create an account to accept this invitation"}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            {status === "loading" && (
              <Loader2 className="h-12 w-12 text-primary animate-spin" />
            )}
            
            {status === "success" && (
              <>
                <CheckCircle2 className="h-12 w-12 text-success" />
                <Button onClick={() => navigate(companyId ? `/app/companies/${companyId}` : "/app")} className="w-full">
                  Go to dashboard
                </Button>
              </>
            )}
            
            {status === "error" && (
              <>
                <XCircle className="h-12 w-12 text-destructive" />
                <Button variant="outline" onClick={() => navigate("/")} className="w-full">
                  Go to home
                </Button>
              </>
            )}
            
            {status === "needsAuth" && (
              <div className="w-full space-y-3">
                <Button onClick={handleLogin} className="w-full">
                  Sign in to accept
                </Button>
                <Button variant="outline" onClick={() => navigate("/")} className="w-full">
                  Go to home
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvitation;